// Tells the note endpoints this page can show interactive widgets — iOS
// WidgetCapability.swift.
//
// The server draws a note's figures one of two ways: a TikZ diagram
// rendered to SVG, or an interactive HTML widget (`![widget:…](…)`, which
// the editor shows as a card, utils/noteWidgets.js). Only a client that
// sends `X-Wellnotes-Widgets: 1` gets widgets; the others keep receiving
// the pictures they already know how to draw.
//
// Unlike the apps, a page must have the endpoint's leave to send a header
// of its own (CORS), and an endpoint deployed before it listed this one in
// `Access-Control-Allow-Headers` would refuse the whole request — the note
// would never be generated. So the page asks first, with an OPTIONS
// request carrying the header: the endpoint answers it before doing
// anything, so asking costs nothing and charges nothing. The header goes
// out only once the endpoint has accepted it.

export const WIDGETS_HEADER = "X-Wellnotes-Widgets";
export const WIDGETS_VALUE = "1";

/** Longest wait for the endpoint's answer before generating without
 *  widgets: the note request itself waits on it. */
export const PROBE_TIMEOUT_MS = 8000;

/**
 * Whether an endpoint accepts the widgets header, asked once per page and
 * endpoint. `fetchImpl` is the network call (the browser's `fetch`; a
 * stand-in in tests).
 */
export function createWidgetCapability(fetchImpl, { timeoutMs = PROBE_TIMEOUT_MS } = {}) {
  const accepted = new Set();
  const asking = new Map();

  function probe(url) {
    return new Promise((resolve) => {
      const controller = typeof AbortController === "function" ? new AbortController() : null;
      const timer = setTimeout(() => {
        if (controller) controller.abort();
        resolve(false);
      }, timeoutMs);
      Promise.resolve()
        .then(() =>
          fetchImpl(url, {
            method: "OPTIONS",
            headers: { [WIDGETS_HEADER]: WIDGETS_VALUE },
            signal: controller ? controller.signal : undefined,
          }),
        )
        .then(
          // Any answer at all means the browser let the header through.
          () => {
            accepted.add(url);
            resolve(true);
          },
          // Refused, offline or too slow: no widgets this time, and the
          // endpoint is asked again next time.
          () => resolve(false),
        )
        .finally(() => {
          clearTimeout(timer);
          asking.delete(url);
        });
    });
  }

  /** Resolves true when `url` takes the header; never rejects. */
  function accepts(url) {
    if (accepted.has(url)) return Promise.resolve(true);
    if (!asking.has(url)) asking.set(url, probe(url));
    return asking.get(url);
  }

  /** `headers` with the widgets header added when `url` accepts it. */
  async function headersFor(url, headers) {
    return (await accepts(url)) ? { ...headers, [WIDGETS_HEADER]: WIDGETS_VALUE } : { ...headers };
  }

  return { accepts, headersFor };
}

export const widgetCapability = createWidgetCapability((url, init) => fetch(url, init));
